import React from 'react';
import { KPI_WEIGHTS, kpiStatus } from '../../utils/vendorKpi';

export function StatusIcon({ value, isPct = true }) {
  const status = isPct ? kpiStatus(value) : (Number(value) <= 5 ? 'good' : Number(value) <= 20 ? 'warn' : 'bad');
  if (status === 'good') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 shrink-0" title="Good">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (status === 'warn') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-600 shrink-0" title="Warning">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L1 21h22L12 2zm0 4l7.5 13h-15L12 6zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z" />
        </svg>
      </span>
    );
  }
  if (status === 'bad') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-600 shrink-0" title="Needs attention">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-8h-2v6h2V8z" />
        </svg>
      </span>
    );
  }
  return null;
}

export function KpiValueCell({ value, isPct = true }) {
  const display = isPct
    ? (value != null ? `${Number(value).toFixed(2)}%` : '—')
    : (value != null ? String(value) : '—');
  return (
    <div className="flex items-center gap-1.5 justify-end">
      <StatusIcon value={value} isPct={isPct} />
      <span>{display}</span>
    </div>
  );
}

export function WeightRow({ columns }) {
  return (
    <tr className="bg-[#F7F5F2] text-[11px] text-sap-textLight">
      <td className="px-4 py-2 font-semibold">Weight</td>
      <td className="px-4 py-2" />
      {columns.map((col) => (
        <td key={col.key} className="px-4 py-2 text-right">
          {KPI_WEIGHTS[col.key] != null ? `${KPI_WEIGHTS[col.key]}%` : '—'}
        </td>
      ))}
    </tr>
  );
}

export function DateFilters({ startDate, endDate, onStartChange, onEndChange, kpiGroup, kpiGroups, onKpiGroupChange, showKpiGroup = true }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {showKpiGroup && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold text-sap-textLight">KPI groups</span>
          <select
            value={kpiGroup}
            onChange={(e) => onKpiGroupChange(e.target.value)}
            className="border border-sap-border rounded-lg px-3 py-1.5 text-sm min-w-[160px] bg-white"
          >
            <option value="All">All</option>
            {(kpiGroups || []).map((g) => (
              <option key={g.label} value={g.label}>{g.label}</option>
            ))}
          </select>
        </label>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold text-sap-textLight">Start date</span>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          className="border border-sap-border rounded-lg px-3 py-1.5 text-sm bg-white"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold text-sap-textLight">End date</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          className="border border-sap-border rounded-lg px-3 py-1.5 text-sm bg-white"
        />
      </label>
    </div>
  );
}

export function MiniLineChart({ points, height = 80, color = '#4A9AF5' }) {
  if (!points?.length) {
    return <div className="h-20 flex items-center justify-center text-xs text-sap-textLight">No trend data</div>;
  }
  const width = 280;
  const pad = { t: 8, r: 8, b: 8, l: 8 };
  const vals = points.map((p) => p.score ?? p.value ?? 0);
  const min = Math.min(...vals) - 1;
  const max = Math.max(...vals) + 1;
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const toY = (v) => pad.t + innerH - ((v - min) / (max - min || 1)) * innerH;
  const toX = (i) => pad.l + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
  const pts = points.map((p, i) => `${toX(i)},${toY(p.score ?? p.value ?? 0)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-[280px] h-20">
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts} />
    </svg>
  );
}

export function ScoreTrendChart({ points, height = 260 }) {
  if (!points?.length) return null;
  const width = 640;
  const pad = { t: 20, r: 20, b: 36, l: 48 };
  const vals = points.map((p) => p.score);
  const min = Math.floor(Math.min(...vals) - 2);
  const max = Math.ceil(Math.max(...vals) + 2);
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const toY = (v) => pad.t + innerH - ((v - min) / (max - min || 1)) * innerH;
  const toX = (i) => pad.l + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
  const linePts = points.map((p, i) => `${toX(i)},${toY(p.score)}`).join(' ');

  const tickStep = Math.max(1, Math.round((max - min) / 4));
  const ticks = [];
  for (let t = min; t <= max; t += tickStep) ticks.push(t);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={pad.l} y1={toY(tick)} x2={width - pad.r} y2={toY(tick)} stroke="#E8EDF2" />
          <text x={pad.l - 6} y={toY(tick) + 4} textAnchor="end" className="fill-sap-textLight text-[10px]">
            {tick.toFixed(1)}%
          </text>
        </g>
      ))}
      {points.filter((_, i) => i % Math.ceil(points.length / 6) === 0 || i === points.length - 1).map((p, idx, arr) => {
        const i = points.indexOf(p);
        return (
          <text key={p.date} x={toX(i)} y={height - 8} textAnchor="middle" className="fill-sap-textLight text-[9px]">
            {new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </text>
        );
      })}
      <polyline fill="none" stroke="#4A9AF5" strokeWidth="2.5" points={linePts} />
    </svg>
  );
}
